import api, { getApiAuthToken } from './api'

export type EmploymentState = 'not_requested' | 'requested' | 'submitted' | 'under_review' | 'verified' | 'rejected' | 'expired' | 'action_required'
export type EmploymentCategory = 'profile' | 'resume' | 'work_authorization' | 'qualification' | 'compliance_document' | 'payroll'
export interface EmploymentRequirementInput {
  jurisdiction: string; category: EmploymentCategory; documentType: string | null
  purpose: string; legalBasis: 'optional_consent' | 'employment_obligation'; legalBasisReference: string | null
  required: boolean; active: boolean; payrollCategory: 'sin' | 'banking' | null; retentionDays: number | null
}
export interface EmploymentRequirement {
  id: string; revision: number; jurisdiction: string; category: EmploymentCategory; document_type: string | null
  purpose: string; legal_basis: EmploymentRequirementInput['legalBasis']; legal_basis_reference: string | null
  required: boolean; active: boolean; payroll_category: 'sin' | 'banking' | null; retention_days: number | null
}
export interface EmploymentRequest {
  id: string; org_id: string; worker_profile_id: string; revision: number; category: EmploymentCategory
  purpose: string; required: boolean; legal_basis: string; legal_basis_reference: string | null
  jurisdiction: string; document_type: string | null; due_at: string | null; state: EmploymentState
  effectiveState: EmploymentState; verified_until: string | null; document_id: string | null
  payroll_category: 'sin' | 'banking' | null
}
export interface EmploymentProfile {
  legalName: string; contactEmail: string | null; contactPhone: string | null
  eligibility: string; authorizationRequired: boolean; revision: number
}
export interface EmploymentSubmission {
  request: EmploymentRequest; profile: EmploymentProfile | null
  document: { id: string; documentType: string; expiryDate: string | null; reviewRevision: string } | null
  learningEvidence: { id: string; evidence_status: string; skill_name: string; expires_at: string | null } | null
}
export interface EmploymentEvent { id: string; event_name: string; created_at: string; metadata: { reason?: string; from?: string; to?: string } }
export interface PayrollCapabilities { deploymentGateOpen: boolean; permissions: { request: boolean; read: boolean; retain: boolean }; productionAcceptance: string }
export interface ComplianceStatus { complete: boolean; truncated: boolean; statuses: { requirementId: string; required: boolean; category: string; state: EmploymentState }[] }
export type DecisionState = 'under_review' | 'verified' | 'rejected' | 'action_required'
export const decisionStates = (state: EmploymentState): DecisionState[] => ({
  submitted: ['under_review', 'rejected', 'action_required'], under_review: ['verified', 'rejected', 'action_required'],
  verified: ['action_required'], rejected: ['action_required'], expired: ['action_required'],
  requested: [], action_required: [], not_requested: [],
}[state] || []) as DecisionState[]

export const employmentError = (error: unknown): string => {
  // Never display/log Axios errors: config may contain protected request data.
  const status = (error as { response?: { status?: number } })?.response?.status
  if (status === 401) return 'Your session expired. Sign in again.'
  if (status === 403) return 'Access or feature unavailable. Refresh your access before continuing.'
  if (status === 404) return 'This record is unavailable in the selected organization.'
  if (status === 409) return 'This record changed. Reload and review before trying again.'
  if (status === 400) return 'Check the required fields and dates.'
  return 'The result could not be confirmed. Reload before retrying.'
}
const organization = (org: string) => `/api/organizations/${encodeURIComponent(org)}/employment`
const requestPath = (id: string) => `/api/employment/requests/${encodeURIComponent(id)}`
export function employmentClient(tenantId: string, signal: AbortSignal) {
  const sessionToken = getApiAuthToken()
  const config = { signal, headers: { 'x-tenant-id': tenantId, 'Cache-Control': 'no-store' } }
  function privateResult<T>(value: T): T {
    if (signal.aborted || document.hidden || sessionToken !== getApiAuthToken()) throw new Error('employment_review_closed')
    return value
  }
  async function get<T>(path: string): Promise<T> {
    const { data } = await api.get(path, config)
    if (sessionToken !== getApiAuthToken()) throw new Error('employment_session_changed')
    if (data?.success !== true) throw new Error('employment_unavailable')
    return data as T
  }
  async function pages<T>(path: string, field: string): Promise<T[]> {
    const rows: T[] = []; const seen = new Set<string>(); let cursor: string | null = null
    do {
      const page: Record<string, unknown> = await get(`${path}${cursor ? `?afterId=${encodeURIComponent(cursor)}` : ''}`)
      if (!Array.isArray(page[field])) throw new Error('employment_unavailable')
      rows.push(...page[field] as T[])
      cursor = typeof page.nextAfterId === 'string' ? page.nextAfterId : null
      if (cursor && seen.has(cursor)) throw new Error('employment_pagination_changed')
      if (cursor) seen.add(cursor)
    } while (cursor)
    return rows
  }
  return {
    requirements: (org: string) => pages<EmploymentRequirement>(`${organization(org)}/requirements`, 'requirements'),
    requests: (org: string) => pages<EmploymentRequest>(`${organization(org)}/requests`, 'requests'),
    saveRequirement: (org: string, id: string, expectedRevision: number, requirement: EmploymentRequirementInput) =>
      api.put(`${organization(org)}/requirements/${encodeURIComponent(id)}`, { expectedRevision, requirement }, config),
    createRequest: (org: string, workerProfileId: string, requirementId: string, dueAt: string | null) =>
      api.post(`${organization(org)}/requests`, { workerProfileId, requirementId, dueAt }, config),
    status: (org: string, worker: string) => get<ComplianceStatus>(`${organization(org)}/workers/${encodeURIComponent(worker)}/status`),
    submission: async (id: string) => privateResult(await get<EmploymentSubmission>(`${requestPath(id)}/submission`)),
    history: (id: string) => pages<EmploymentEvent>(`${requestPath(id)}/history`, 'events'),
    decide: (row: EmploymentRequest, state: DecisionState, reason: string, verifiedUntil: string | null) =>
      api.post(`${requestPath(row.id)}/decision`, { expectedRevision: row.revision, state, reason, verifiedUntil }, config),
    document: async (id: string) => privateResult((await api.get(`${requestPath(id)}/document/file`, { ...config, responseType: 'blob' })).data as Blob),
    payrollCapabilities: (org: string) => get<PayrollCapabilities>(`/api/protected-payroll/capabilities?orgId=${encodeURIComponent(org)}`),
    payrollMask: async (row: EmploymentRequest) => {
      const { data } = await api.post(`/api/protected-payroll/requests/${encodeURIComponent(row.id)}/access`, {
        purpose: row.purpose, expectedRequestRevision: row.revision, reveal: false,
      }, config)
      return privateResult(data as { value: { sin?: string; accountNumber?: string } })
    },
    payrollDecision: (row: EmploymentRequest, state: DecisionState, reason: string, verifiedUntil: string | null) =>
      api.post(`/api/protected-payroll/requests/${encodeURIComponent(row.id)}/decision`, { expectedRevision: row.revision, state, reason, verifiedUntil }, config),
  }
}
